import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackAffiliateClick } from "../api/affiliate";
import { getVisitorId, readRefParam, storeReferral } from "../utils/referral";

/**
 * Captures `?ref=<code>` on any route — affiliates often paste the mint space URL with the
 * code appended rather than the /r/<code> short link. Records the click, stores the code,
 * then strips the param so it doesn't linger in the URL the user might re-share.
 */
export default function useReferralCapture() {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef("");

  useEffect(() => {
    const code = readRefParam(location.search);
    if (!code || handledRef.current === code) return;
    handledRef.current = code;

    // Store immediately: even if tracking fails the attribution should still work.
    storeReferral(code);

    let active = true;
    (async () => {
      try {
        const res = await trackAffiliateClick(code, getVisitorId());
        if (active && res?.success && res.data?.mintSpaceId) {
          storeReferral(res.data.code || code, {
            mintSpaceId: res.data.mintSpaceId,
            windowDays: res.data.cookieWindowDays,
          });
        }
      } catch {
        /* unknown code — the backend rejects it at join time anyway */
      }
    })();

    const params = new URLSearchParams(location.search);
    params.delete("ref");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}${location.hash || ""}`, {
      replace: true,
    });

    return () => {
      active = false;
    };
  }, [location.search, location.pathname, location.hash, navigate]);
}
